import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { EiqPagination } from 'eiq-storybook';
import { toSimpleUtcDateString } from 'src/utils/format/date';
import ReactTooltip from 'react-tooltip';
import Loader from 'src/components/Loader';
import { PieChart } from 'react-minimal-pie-chart';
import { makeJobUrl } from 'src/routes/routes';
import { filterJobType } from 'src/utils/data/job';
import { useData } from './hooks';
import s from '../Dashboards.styles';

export const ReportReviewerDashboardWidget = () => {
  const {
    jobs,
    reportReviewerJobsLoading,
    totalReportReviewerJobs,
    reFetchNextReportReviewerJobsBatch,
  } = useData();

  const [
    reportReviewerJobsOffsetMovedBy,
    setReportReviewerJobsOffsetMovedBy,
  ] = useState(0);
  const [
    reportReviewerJobPageCounter,
    setReportReviewerJobPageCounter,
  ] = useState(1);

  const { t } = useTranslation();

  const reportReviewerJobsPrevInactive =
    reportReviewerJobsOffsetMovedBy === 0 || totalReportReviewerJobs === 0;
  const reportReviewerJobsNextInactive =
    reportReviewerJobsOffsetMovedBy + jobs.length === totalReportReviewerJobs ||
    totalReportReviewerJobs === 0;

  const JobStatusComponent = ({ job: { status } }) => {
    if (status === 'under_review') {
      return (
        <s.JobStatusSpan underReview={true}>
          <Trans>REPORT_REVIEWER_DASHBOARD_UNDER_REVIEW</Trans>
        </s.JobStatusSpan>
      );
    }
    if (status === 'ready_for_review') {
      return (
        <s.JobStatusSpan readyForReview={true}>
          <Trans>REPORT_REVIEWER_DASHBOARD_READY_FOR_REVIEW</Trans>
        </s.JobStatusSpan>
      );
    }
    return null;
  };

  JobStatusComponent.propTypes = {
    job: PropTypes.shape({
      status: PropTypes.string,
    }),
  };

  const pieChart = job => {
    const closedNoteCount = job.reportReviewClosedNotesCount;
    const allNoteCount = job.reportReviewAllNotesCount;

    return (
      <s.Status flex>
        <s.PieChartDiv
          data-for="ReportReviewerDashboardWidget"
          data-tip={t('REPORT_REVIEWER_DASHBOARD_NOTES_CLOSED')}
        >
          <PieChart
            data={[
              {
                color: '#15af4c',
                title: t('AUDITOR_DASHBOARD_CLOSED_NOTE_COUNT'),
                value: allNoteCount === 0 ? 0 : closedNoteCount,
              },
              {
                color: '#d9d9d9',
                title: t('AUDITOR_DASHBOARD_OPEN_NOTE_COUNT'),
                value: allNoteCount === 0 ? 1 : allNoteCount - closedNoteCount,
              },
            ]}
            lineWidth={15}
            radius={50}
            startAngle={-90}
            lengthAngle={360}
            animate={false}
            animationDuration={500}
            animationEasing="ease-out"
            label={() => null}
            style={{
              height: '25px',
              width: '25px',
              transform: 'rotate(-90deg)',
            }}
            viewBoxSize={[100, 100]}
            segmentsStyle={{
              transition: 'stroke .3s ease',
            }}
            segmentsShift={0}
          />
          <s.PieChartText>
            {allNoteCount === 0 ? 0 : `${closedNoteCount}/${allNoteCount}`}
          </s.PieChartText>
        </s.PieChartDiv>
      </s.Status>
    );
  };

  return (
    <Fragment>
      <s.DashboardComponent>
        <s.DashboardComponentTitle widget>
          <Trans>REPORT_REVIEWER_DASHBOARD_JOBS_WITH_REPORT_REVIEW</Trans>
        </s.DashboardComponentTitle>
        {reportReviewerJobsLoading ? (
          <Loader />
        ) : (
          <s.WidgetDiv>
            <ReactTooltip id="ReportReviewerDashboardWidget" place="bottom" />
            {jobs.map(job => (
              <s.WidgetCard key={job.jobId}>
                <s.WidgetCardInnerDiv>
                  {pieChart(job)}
                  <s.JobName to={makeJobUrl(job.jobId)}>
                    {job.reportNumber}
                  </s.JobName>
                  <s.FactoryName>{job.factoryName}</s.FactoryName>
                  <JobStatusComponent job={job} />
                  {!job.reviewerId && (
                    <s.JobStatusSpan notAssigned>
                      <Trans>REPORT_REVIEWER_DASHBOARD_NOT_ASSIGNED</Trans>
                    </s.JobStatusSpan>
                  )}
                  {job.actionRequired && (
                    <s.JobStatusSpan actionRequired={true}>
                      <Trans>AUDITOR_DASHBOARD_ACTION_REQUIRED</Trans>
                    </s.JobStatusSpan>
                  )}
                  <s.CardInfoWithMargin>
                    <s.ServicesName
                      data-for="ReportReviewerDashboardWidget"
                      data-tip={
                        job.serviceNames && job.serviceNames.length > 0
                          ? job.serviceNames.join(', ')
                          : 'N/A'
                      }
                    >
                      <s.ServiceButtonBookIcon name="serviceIconBlue" />
                      {job.serviceNames && job.serviceNames.length > 0
                        ? job.serviceNames.join(', ')
                        : 'N/A'}
                    </s.ServicesName>
                  </s.CardInfoWithMargin>
                  <s.DateAndAssignedTo>
                    <s.DateAndAssignedToLeft>
                      <s.CardSmallTitle>
                        {filterJobType('SAQ', job.serviceNames).includes(
                          true,
                        ) ? (
                          <Trans>SAQ_DATE</Trans>
                        ) : (
                          <Trans>AUDITOR_DASHBOARD_AUDIT_START_END_DATE</Trans>
                        )}
                      </s.CardSmallTitle>
                      <s.CardSmallValue
                        data-for="ReportReviewerDashboardWidget"
                        data-tip={`${toSimpleUtcDateString(
                          job.auditStartAt,
                        )} / ${toSimpleUtcDateString(job.auditEndAt)}`}
                      >
                        <s.AuditStartDate>
                          {toSimpleUtcDateString(job.auditStartAt)}
                        </s.AuditStartDate>
                        <s.AuditStartDate>
                          {!filterJobType('SAQ', job.serviceNames).includes(
                            true,
                          )
                            ? ` / ${toSimpleUtcDateString(job.auditEndAt)}`
                            : ''}
                        </s.AuditStartDate>
                      </s.CardSmallValue>
                    </s.DateAndAssignedToLeft>
                    <s.DateAndAssignedToRight>
                      {
                        <Fragment>
                          <s.CardSmallTitle>
                            <Trans>AUDITOR_DASHBOARD_ASSIGNED_TO</Trans>
                          </s.CardSmallTitle>
                          <s.CardSmallValue
                            data-for="ReportReviewerDashboardWidget"
                            data-tip={
                              job.auditorNames.length === 0
                                ? 'N/A'
                                : job.auditorNames.join(', ')
                            }
                          >
                            {job.auditorNames.length === 0
                              ? 'N/A'
                              : job.auditorNames.join(', ')}
                          </s.CardSmallValue>
                        </Fragment>
                      }
                    </s.DateAndAssignedToRight>
                  </s.DateAndAssignedTo>
                </s.WidgetCardInnerDiv>
              </s.WidgetCard>
            ))}
            <s.CornerNavigationContainer>
              <EiqPagination
                dataCount={totalReportReviewerJobs}
                showPerPage={false}
                pageSize={18}
                canPreviousPage={!reportReviewerJobsPrevInactive}
                canNextPage={!reportReviewerJobsNextInactive}
                gotoPage={pageIndex => {
                  const newCountOfReportReviewerJobsOffset = pageIndex * 18;
                  setReportReviewerJobsOffsetMovedBy(
                    newCountOfReportReviewerJobsOffset,
                  );
                  reFetchNextReportReviewerJobsBatch(
                    newCountOfReportReviewerJobsOffset,
                  );
                  setReportReviewerJobPageCounter(pageIndex + 1);
                }}
                pageIndex={reportReviewerJobPageCounter - 1}
                nextPage={() => {
                  if (reportReviewerJobsNextInactive) {
                    return;
                  }
                  const newCountOfReportReviewerJobsOffset =
                    reportReviewerJobsOffsetMovedBy + 18;
                  setReportReviewerJobsOffsetMovedBy(
                    newCountOfReportReviewerJobsOffset,
                  );
                  reFetchNextReportReviewerJobsBatch(
                    newCountOfReportReviewerJobsOffset,
                  );
                  setReportReviewerJobPageCounter(
                    reportReviewerJobPageCounter + 1,
                  );
                }}
                previousPage={() => {
                  if (reportReviewerJobsPrevInactive) {
                    return;
                  }
                  const newCountOfReportReviewerJobsOffset =
                    reportReviewerJobsOffsetMovedBy - 18;
                  setReportReviewerJobsOffsetMovedBy(
                    newCountOfReportReviewerJobsOffset,
                  );
                  reFetchNextReportReviewerJobsBatch(
                    newCountOfReportReviewerJobsOffset,
                  );
                  setReportReviewerJobPageCounter(
                    reportReviewerJobPageCounter - 1,
                  );
                }}
              />              
            </s.CornerNavigationContainer>
          </s.WidgetDiv>
        )}
      </s.DashboardComponent>
    </Fragment>
  );
};

export default React.memo(ReportReviewerDashboardWidget);
